import { z } from 'zod';

/**
 * Input sanitization utilities for BeastMCP tools
 */
export class InputSanitizer {
  /**
   * Sanitize string input by removing potentially dangerous characters
   */
  static sanitizeString(input: string, options: {
    maxLength?: number;
    allowHtml?: boolean;
    allowScripts?: boolean;
  } = {}): string {
    let sanitized = input;

    // Remove HTML tags if not allowed
    if (!options.allowHtml) {
      sanitized = sanitized.replace(/<[^>]*>/g, '');
    }

    // Remove script tags and javascript: URLs
    if (!options.allowScripts) {
      sanitized = sanitized.replace(/<script[^>]*>.*?<\/script>/gi, '');
      sanitized = sanitized.replace(/javascript:/gi, '');
      sanitized = sanitized.replace(/on\w+\s*=/gi, '');
    }

    // Limit length
    if (options.maxLength && sanitized.length > options.maxLength) {
      sanitized = sanitized.substring(0, options.maxLength);
    }

    return sanitized.trim();
  }

  /**
   * Sanitize object input recursively
   */
  static sanitizeObject(
    obj: Record<string, any>,
    options: {
      maxStringLength?: number;
      allowHtml?: boolean;
      allowScripts?: boolean;
    } = {}
  ): Record<string, any> {
    const sanitized = { ...obj };

    for (const [key, value] of Object.entries(sanitized)) {
      if (typeof value === 'string') {
        sanitized[key] = this.sanitizeString(value, {
          maxLength: options.maxStringLength,
          allowHtml: options.allowHtml,
          allowScripts: options.allowScripts
        });
      } else if (typeof value === 'object' && value !== null) {
        sanitized[key] = this.sanitizeObject(value, options);
      }
    }

    return sanitized;
  }

  /**
   * Validate and sanitize input using a zod schema
   */
  static validateAndSanitize<T>(
    input: unknown,
    schema: z.ZodSchema<T>,
    options: {
      maxStringLength?: number;
      allowHtml?: boolean;
      allowScripts?: boolean;
    } = {}
  ): T {
    // First validate with zod
    const validated = schema.parse(input);

    // Then sanitize if it's an object
    if (typeof validated === 'object' && validated !== null) {
      return this.sanitizeObject(validated, options) as T;
    }

    // For primitive types, just return the validated value
    return validated;
  }

  /**
   * Create a sanitized zod schema for strings
   */
  static createSanitizedStringSchema(
    options: {
      maxStringLength?: number;
      allowHtml?: boolean;
      allowScripts?: boolean;
    } = {}
  ): z.ZodEffects<z.ZodString, string, string> {
    return z.string().transform((data) => {
      return this.sanitizeString(data, options);
    });
  }
}

/**
 * Common sanitization patterns
 */
export const SanitizationPatterns = {
  /**
   * Remove all HTML tags
   */
  removeHtml: (input: string): string => {
    return input.replace(/<[^>]*>/g, '');
  },

  /**
   * Remove script tags and javascript URLs
   */
  removeScripts: (input: string): string => {
    return input
      .replace(/<script[^>]*>.*?<\/script>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '');
  },

  /**
   * Escape HTML entities
   */
  escapeHtml: (input: string): string => {
    return input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
  },

  /**
   * Normalize whitespace
   */
  normalizeWhitespace: (input: string): string => {
    return input.replace(/\s+/g, ' ').trim();
  },

  /**
   * Remove control characters
   */
  removeControlChars: (input: string): string => {
    return input.replace(/[\x00-\x1F\x7F]/g, '');
  }
};

/**
 * Predefined sanitization options for common use cases
 */
export const SanitizationOptions = {
  /**
   * Strict sanitization for user input
   */
  strict: {
    maxStringLength: 1000,
    allowHtml: false,
    allowScripts: false
  },

  /**
   * Moderate sanitization for trusted content
   */
  moderate: {
    maxStringLength: 10000,
    allowHtml: true,
    allowScripts: false
  },

  /**
   * Permissive sanitization for internal data
   */
  permissive: {
    maxStringLength: 100000,
    allowHtml: true,
    allowScripts: true
  }
}; 